import { RRule, rrulestr } from 'rrule';
import { ReserveScheduleRuleParts } from '../types/reserveSchedule';
import { dateToIsoUTC, endOfDayUTC, getLocaleDateStringFromIso, isoToDateUTC } from './time';

export const MAX_RRULE_DATES = 31 * 3; // 3 months max

export type RRuleFrequency = 'NEVER' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
export const RRULE_REPEAT_FREQ: RRuleFrequency[] = [
  'NEVER',
  'DAILY',
  'WEEKLY',
  'MONTHLY',
  'YEARLY',
];

export type RRuleBySetPos = 1 | 2 | 3 | 4 | -1;
export const RRULE_BYSET_POS: RRuleBySetPos[] = [1, 2, 3, 4, -1];
export type RRuleBySetPosLabel = 'First' | 'Second' | 'Third' | 'Fourth' | 'Last';

export type RRuleByDayBase = 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU';
export type RRuleByDay = RRuleByDayBase | 'MO,TU,WE,TH,FR' | 'SA,SU' | 'MO,TU,WE,TH,FR,SA,SU';
export const RRULE_BYDAY_BASE: RRuleByDayBase[] = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
export const RRULE_BYDAY: RRuleByDay[] = [
  ...RRULE_BYDAY_BASE,
  'MO,TU,WE,TH,FR',
  'SA,SU',
  'MO,TU,WE,TH,FR,SA,SU',
];
export type RRuleByDayLabel =
  | 'Mon'
  | 'Tue'
  | 'Wed'
  | 'Thu'
  | 'Fri'
  | 'Sat'
  | 'Sun'
  | 'Weekday (Mon-Fri)'
  | 'Weekend (Sat-Sun)'
  | 'Every Day (Sun-Sat)';

export type RRuleEndType = 'NEVER' | 'ON DATE' | 'AFTER OCCURRENCES';
export const RRULE_END_TYPE: RRuleEndType[] = ['NEVER', 'ON DATE', 'AFTER OCCURRENCES'];

export function rruleBySetPosValueToLabel(bySetPos: RRuleBySetPos): RRuleBySetPosLabel {
  switch (bySetPos) {
    case 1:
      return 'First';
    case 2:
      return 'Second';
    case 3:
      return 'Third';
    case 4:
      return 'Fourth';
    case -1:
    default:
      return 'Last';
  }
}

export function rruleByDayValueToLabel(byDay: RRuleByDay): RRuleByDayLabel {
  switch (byDay) {
    case 'MO':
      return 'Mon';
    case 'TU':
      return 'Tue';
    case 'WE':
      return 'Wed';
    case 'TH':
      return 'Thu';
    case 'FR':
      return 'Fri';
    case 'SA':
      return 'Sat';
    case 'SU':
      return 'Sun';
    case 'MO,TU,WE,TH,FR':
      return 'Weekday (Mon-Fri)';
    case 'SA,SU':
      return 'Weekend (Sat-Sun)';
    case 'MO,TU,WE,TH,FR,SA,SU':
    default:
      return 'Every Day (Sun-Sat)';
  }
}

export function rruleFromScheduleRule(options: ReserveScheduleRuleParts): RRule {
  const startPart = options.start.replace(/-/g, '').slice(0, 8);
  const endPart = options.until.replace(/-/g, '').slice(0, 8);

  let strBuilder = `DTSTART:${startPart}T000000Z`;

  if (options.frequency === 'NEVER') {
    // rrulestr with only DTSTART defaults to YEARLY — explicitly cap to one occurrence
    strBuilder += `\nRRULE:FREQ=DAILY;COUNT=1`;
  } else {
    strBuilder +=
      `\nRRULE:FREQ=${options.frequency};` +
      (options.frequency === 'WEEKLY' ? `BYDAY=${options.byDay};` : '') +
      (options.frequency === 'MONTHLY'
        ? `${
            options.isDynamicSetPos
              ? `BYSETPOS=${options.bySetPos};BYDAY=${options.byDay};`
              : `BYMONTHDAY=${options.byMonthDay};`
          }`
        : '') +
      (options.frequency === 'YEARLY'
        ? `${
            options.isDynamicSetPos
              ? `BYSETPOS=${options.bySetPos};BYDAY=${options.byDay};BYMONTH=${options.byMonth};`
              : `BYMONTHDAY=${options.byMonthDay};BYMONTH=${options.byMonth};`
          }`
        : '') +
      (options.endType === 'AFTER OCCURRENCES' ? `COUNT=${options.count};` : '') +
      (options.endType === 'ON DATE' ? `UNTIL=${endPart}T000000Z;` : '') +
      `INTERVAL=${options.interval}`;
  }

  return rrulestr(strBuilder);
}

export function rruleToText(rule: RRule): string {
  const startDate = dateToIsoUTC(rule.options.dtstart);
  const dateStr = getLocaleDateStringFromIso(startDate, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const str =
    !rule.origOptions.freq || rule.origOptions.count === 1
      ? dateStr
      : `${rule.toText()} from ${dateStr}`;
  return str[0].toUpperCase() + str.substring(1);
}

export function splitRuleTextToMultiLines(text: string): string[] {
  const match = text.match(/( from | for | until )/);
  if (match && match.index !== undefined) {
    // Split at the start of the match (e.g. " for")
    const splitIndex = match.index;
    const part1 = text.substring(0, splitIndex).trim();
    const part2 = text.substring(splitIndex).trim();
    return [part1, part2];
  }
  return [text];
}

export function rruleCheckDate(rrule: RRule, dateIso: string): boolean {
  const dayStart = isoToDateUTC(dateIso);
  const dayEnd = endOfDayUTC(isoToDateUTC(dateIso));

  return rrule.between(dayStart, dayEnd, true).length > 0;
}

export function rruleNextDate(rrule: RRule, dateIso: string): string | null {
  const dayStart = isoToDateUTC(dateIso);
  const after = rrule.after(dayStart);

  return !after ? null : dateToIsoUTC(after);
}

export function rrulePreviousDate(rrule: RRule, dateIso: string): string | null {
  const dayStart = isoToDateUTC(dateIso);
  const before = rrule.before(dayStart);

  return !before ? null : dateToIsoUTC(before);
}
